import { defineStore } from 'pinia';
import { objLen } from 'src/utils/general';
import type { dynamicObj } from 'src/pages/assets';
import { useSettingStore } from 'src/store/main';
import { docScriptApiService } from 'src/services';
import { isUserAdmin } from 'src/utils/UserDetails';

export const useDocScriptStore = defineStore('doc_script', {
  state: () => ({
    current_model: {} as dynamicObj,
    processing: false,
    selected_doc_type: '',
    sub_doc_type_key: '',
    record_id: '',
    display_output_view: false,
    script_output: '',
  }),
  getters: {
    is_script_editor_available() {
      const settings_store = useSettingStore();
      // Access user_settings directly since getters cannot be async
      // Settings should already be loaded by router before this is accessed
      const DOC_SCRIPT_MODULE = settings_store.user_settings['DOC_SCRIPT_MODULE_SETTING'];
      if (!DOC_SCRIPT_MODULE) {
        return false;
      }

      if (isUserAdmin()) {
        return true;
      }

      return false;
    },
  },
  actions: {
    async saveCurrentModel() {
      const data = {} as dynamicObj;
      for (const [key, value] of Object.entries(this.current_model)) {
        let transformed_value = value;
        if (key == 'script_data') {
          transformed_value = JSON.stringify(value);
        }

        data[key] = transformed_value;
      }
      const response = await docScriptApiService.createUpdateScript(data);
      if (!response || response.success == false) {
        throw response;
      }

      if (response.data.hasOwnProperty('id')) {
        this.setCurrentModel(response.data);
      }

      // If a draft was created/updated, set the version_id for subsequent saves
      if (response.draft && response.draft.id) {
        this.current_model.version_id = response.draft.id;
      }

      return response;
    },

    async getScript() {
      const doc_type = this.selected_doc_type;
      try {
        const response = await docScriptApiService.getByDocType(doc_type);

        if (!Array.isArray(response) || objLen(response[0]) == 0) {
          this.setEmptyModel();
          return;
        }

        this.setCurrentModel(response[0]);
      } catch (err) {
        throw err;
      }
    },

    async getScriptById() {
      try {
        const response = await docScriptApiService.getByID(this.record_id);

        if (!response) {
          this.setEmptyModel();
          return;
        }

        this.setCurrentModel(response);
      } catch (err) {
        // Don't call setEmptyModel() here - it would wipe out fallback metadata
        // that the component may have pre-populated before calling this method
        throw err;
      }
    },

    setCurrentModel(response: dynamicObj) {
      if (response.hasOwnProperty('script_data')) {
        response['script_data'] = JSON.parse(response['script_data']);
      }
      // Merge response into current_model instead of replacing it
      // This preserves any pre-populated fields (like label from fallback metadata)
      const merged = { ...this.current_model, ...response };

      // Preserve fallback values for critical fields if API returns empty/falsy values
      // This handles cases where backend may return empty strings instead of actual values
      const fieldsToPreserve = ['label', 'doc_type', 'sub_doc_type'];
      console.log('setCurrentModel - BEFORE preserve:', {
        'current_model.label': this.current_model.label,
        'response.label': response.label,
        'merged.label': merged.label,
      });

      for (const field of fieldsToPreserve) {
        if (!response[field] && this.current_model[field]) {
          console.log(`Preserving ${field}: from ${this.current_model[field]}`);
          merged[field] = this.current_model[field];
        }
      }

      console.log('setCurrentModel - AFTER preserve:', {
        'merged.label': merged.label,
      });

      this.current_model = merged;
    },

    setEmptyModel() {
      return (this.current_model = {
        id: this.record_id,
        script_data: '',
        doc_type: this.selected_doc_type,
        sub_doc_type: this.sub_doc_type_key,
        trigger_on: 'ALL',
        label: '',
      } as dynamicObj);
    },

    reset() {
      this.current_model = {} as dynamicObj;
      this.selected_doc_type = '';
      this.sub_doc_type_key = '';
      this.record_id = '';
    },

    async restoreVersion(version_id: string) {
      this.processing = true;
      try {
        const response = await docScriptApiService.restoreVersion(version_id);

        if (!response || response.success === false) {
          throw response;
        }

        // Reload current script with restored content
        await this.getScriptById();

        return response.data;
      } catch (error) {
        throw error;
      } finally {
        this.processing = false;
      }
    },
  },
});
